import React, { useContext } from "react";
import { useI18n } from "i18n";
import { Errors, trackError, trackWarning, Warnings } from "modules/monitoring";
import useNotificationActions from "modules/notification/hooks/useNotificationActions";
import { Events, TRACKING_CONTEXT } from "modules/tracking";
import { addWarningNotification } from "modules/notification/redux/notificationSlice";
import { useAppDispatch } from "store/hooks";
import AuthButton from "./AuthButton";
import { useStyles } from "./LinkedInSSO.styles";
import LinkedInLogoBlue from "../assets/linkedin_logo_blue.svg";
import signInWithProvider from "../utils/signInWithProvider";
import { PROVIDERS } from "../constants";
import { PreferredLoginError } from "../types";
import { showUserPreferredLoginBypass } from "../redux/authSlice";

interface Props {
  text: string;
  isEventHappeningNow?: boolean; // only when used in event landing
}

const LinkedInSSOButton = ({ text, isEventHappeningNow }: Props) => {
  const { track } = useContext(TRACKING_CONTEXT);
  const { t } = useI18n(["auth", "event"]);
  const classes = useStyles();
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const { addErrorNotification } = useNotificationActions();
  const dispatch = useAppDispatch();

  const login = async () => {
    if (isEventHappeningNow !== undefined) {
      track(Events.LOGIN_CLICKED_LINKEDIN, {
        eventStatus: isEventHappeningNow ? "ongoing" : "upcoming",
      });
    }

    setIsLoading(true);

    try {
      await signInWithProvider(PROVIDERS.LINKEDIN, { usePopup: true });
    } catch (error) {
      if (error instanceof PreferredLoginError) {
        dispatch(showUserPreferredLoginBypass(error.methods));
        dispatch(
          addWarningNotification({
            message: t("event:preferred.login.nudge"),
          }),
        );
      } else if (error instanceof Error) {
        addErrorNotification({
          message: error.message,
          autoDismiss: 10,
        });

        trackWarning(Warnings.LINKEDIN_SSO, { error });
      } else {
        addErrorNotification({
          message: t("error.unknown"),
          autoDismiss: 10,
        });
        trackError(error, { label: Errors.UNEXPECTED_LINKEDIN_SSO_ERROR });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthButton
      onClick={login}
      data-testid="linkedin-sso-button"
      startAdornment={
        <img
          alt={text}
          className={classes.linkedInLogo}
          src={LinkedInLogoBlue}
        />
      }
      loading={isLoading}
    >
      {text}
    </AuthButton>
  );
};

export default LinkedInSSOButton;
